/** @module csi/store/CustomViews */
define([
	"dojo/_base/declare",
	"csi",
	"csi/comm",
	"csi/prefs/PreferenceView",
	"csi/xml",
	"./GroupQueryEngine",
	"./Request"
], function(declare, csi, comm, PreferenceView, xml, GroupQueryEngine, RequestStore) {

	/**
	 * Data store holding items of type csi/prefs/PreferenceView
	 */

	return declare(RequestStore, {
		cssProperty: "css",	// necessary to apply group styling in custom view drop-down
		idProperty: "urlNode",
		labelProperty: "name",

		/** @property {string} containerUrl - URL of _CS_PreferenceContainer holding custom views (required) */
		containerUrl: null,

		/** @property {PreferenceView} defaultView - default custom view for container (required) */
		defaultView: null,

		/** @property {ICache} partyCache - cache where to store parties assigned to custom views */
		partyCache: null,

		/** @property {StoreNames} playableRolesStore - cache where to store parties assigned to custom views */
		playableRolesStore: null,

		/**
		 * Create new PreferenceView item for store
		 * @param {string} id
		 * @param {Object} values
		 * @param {string} [name]
		 * @param {boolean} [personal]
		 * @return {PreferenceView}
		 */
		createItem: function(id, values, name, personal) {
			const item = this.defaultView.newInstance();

			item[this.idProperty] = id;
			item.setName(name || "");
			item.setValues(values);

			if (personal) {
				item.setCurrentUser();
			}

			return item;
		},

		/**
		 * @override
		 * If request for whole store has not yet executed, then issue request for single item
		 */
		get: function(id) {
			if (!id) {
				return null;
			}

			// if item is already loaded, then return it
			const item = this._store.get(id);
			if (item) {
				return item;
			}

			// if request is in progress, then wait for it to return before satisfying query
			if (this._dfdRequest) {
				return this.inherited(arguments);
			}

			const oneRequest = comm.getNodeRequest(id).addDependencyPaths([xml.path("SourceView")]);

			return oneRequest.go().then(res => {
				const resultNode = res.getResultNode();
				if (!resultNode) {
					return null;
				}

				// add item to store for source view, if not there already
				const sourceUrl = resultNode.SourceView;
				if (sourceUrl != csi.EMPTY_URL) {
					const sourceItem = this._store.get(sourceUrl);
					const sourceNode = res.getNode(sourceUrl);

					if (!sourceItem && sourceNode) {
						this._createItemFromNode(sourceNode);
					}
				}

				return this._createItemFromNode(resultNode);
			});
		},

		/**
		 * Return custom options for view
		 * @return {Object[]}
		 */
		getCustomOptions: function() {
			return this.defaultView.customOptions;
		},

		getLoadedViews: function() {
			return this._store.query();
		},

		/**
		 * Stores are cached and so may be reused across views that have different defaults, so update store template if given
		 * @param {PreferenceView} [defaultView]
		 */
		setDefaultView: function(defaultView) {
			if (defaultView) {
				this.put(defaultView);
			}

			this.defaultView = defaultView;
		},

		/**
		 * Create and add PreferenceView item to store
		 * @param {Node} node
		 * @return {PreferenceView}
		 */
		_createItemFromNode: function(node) {
			const {defaultView, _store} = this;
			const item = defaultView.newInstance();
			const sourceUrl = node.SourceView;
			const sourceItem = (sourceUrl === csi.EMPTY_URL) ? null : _store.get(sourceUrl);
			console.assert((sourceUrl === csi.EMPTY_URL) || !!sourceItem);
			item.load(node, sourceItem);
			this.put(item);
			return item;
		},

		/**
		 * @override
		 * If no sort options provided, then sort by name
		 */
		_doQuery: function(query, options) {
			options = options || {};
			options.sort = options.sort || [{attribute: this.labelProperty}];
			return this.inherited(arguments, [query, options]);
		},

		/**
		 * @override
		 * Convert nodes in response to PreferenceView items
		 */
		_onResponse: function(res, req, options) {
			const {idProperty, partyCache, _store} = this;

			// remove all entries except for default
			_store.query().forEach(item => {
				const id = item[idProperty];

				if (id !== csi.EMPTY_URL) {
					_store.remove(id);
				}
			});

			// add results to store - make sure to sort them by shared status so that shared views
			// are loaded first; this is done so source views will be loaded before personalized
			// views
			const nodes = res.getResultNodes();
			const sortedNodes = csi.sort(nodes, node => !node.IsShared);
			sortedNodes.forEach(this._createItemFromNode, this);

			// cache UMEntry nodes from custom view in designated cache
			if (partyCache) {
				res.getNonResultNodes()?.forEach((node) => {
					partyCache.cacheNode(node);
				});
			}
		},

		/**
		 * @override
		 * Use GroupQueryEngine for internal store
		 */
		_onStoreOptions: function(options) {
			options.queryEngine = GroupQueryEngine;
		}
	});
});
